import { ChartLine } from "lucide-react";
import { Badge, cn } from "web-design-system";
import {
  PoorlyPerformingFund,
  PoorlyPerformingReason,
  PortfolioReviewBlock,
} from "creso-core/types";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { Link } from "@tanstack/react-router";
import {
  ReviewBlockState,
  ReviewEmptyState,
  ReviewSection,
} from "./review-section";

const REASON_LABEL: Record<PoorlyPerformingReason, string> = {
  LOW_QUARTILE: "Low 3Y quartile rank in category",
  BELOW_BENCHMARK: "Underperforming benchmark",
};

const ROMAN = ["i", "ii", "iii", "iv"];

export function PoorlyPerformingFunds({
  block,
  isLoading,
}: {
  block?: PortfolioReviewBlock<PoorlyPerformingFund[]>;
  isLoading: boolean;
}) {
  const funds = block?.data;

  return (
    <ReviewSection title="Poorly performing funds">
      <ReviewBlockState
        isLoading={isLoading}
        isError={block?.ok === false}
        isEmpty={!!funds && !funds.length}
        emptyState={
          <ReviewEmptyState icon={<ChartLine />} tone="positive">
            No poorly performing funds in these holdings
          </ReviewEmptyState>
        }
      >
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-200 text-sm">
            <thead className="bg-card">
              <tr>
                <th className="w-90 p-4 text-left font-semibold">Fund Name</th>
                {[
                  "Source",
                  "Current",
                  "3Y Trailing",
                  "Sharpe",
                  "3Y Quartile",
                ].map((label) => (
                  <th key={label} className="p-4 text-right font-semibold">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            {funds?.map((fund) => (
              <PoorlyPerformingRow key={fund.scheme_isin} fund={fund} />
            ))}
          </table>
        </div>
      </ReviewBlockState>
    </ReviewSection>
  );
}

function PoorlyPerformingRow({ fund }: { fund: PoorlyPerformingFund }) {
  const alpha = fund.alpha_3y === null ? null : Number(fund.alpha_3y);
  return (
    <tbody className="border-t border-border">
      <tr className="[&>td]:px-4 [&>td]:py-3 [&>td]:text-right [&>td:first-child]:text-left">
        <td>
          <div className="flex items-center gap-3">
            <AmcLogo amc_code={fund.amc_morningstar_code} />
            <div className="flex min-w-0 flex-col gap-1">
              <Link
                to="/funds/$fund_id"
                params={{ fund_id: fund.fund_id }}
                className="truncate"
              >
                {fund.scheme_name}
              </Link>
              <span className="truncate text-xs text-muted-foreground">
                {fund.category}
              </span>
            </div>
          </div>
        </td>
        <td>
          <Badge
            variant="custom"
            className="bg-tag-slate font-semibold text-tag-slate-foreground"
          >
            {fund.source_label}
          </Badge>
        </td>
        <td>
          <div className="flex flex-col items-end gap-1">
            <span>{convertToCurrency(fund.current_value)}</span>
            <span className="text-xs text-muted-foreground">
              {formatAsPercentage(fund.pct_of_aum, "-", 0)} of AUM
            </span>
          </div>
        </td>
        <td>
          <div className="flex flex-col items-end gap-1">
            <span>{formatAsPercentage(fund.return_3y, "-")}</span>
            {alpha !== null ? (
              <Badge
                variant="custom"
                className={cn(
                  "px-1 text-xxs font-normal",
                  alpha < 0
                    ? "bg-tag-red text-tag-red-foreground"
                    : "bg-tag-green text-tag-green-foreground"
                )}
              >
                {alpha > 0 ? "+" : ""}
                {formatAsPercentage(alpha, "-", 1)} alpha
              </Badge>
            ) : null}
          </div>
        </td>
        <td>
          {fund.sharpe_3y === null ? "-" : Number(fund.sharpe_3y).toFixed(2)}
        </td>
        <td>
          {fund.quartile ? (
            <Badge
              variant="custom"
              className={cn(
                "font-semibold",
                fund.quartile >= 3
                  ? "bg-tag-red text-tag-red-foreground"
                  : "bg-tag-slate text-tag-slate-foreground"
              )}
            >
              Q{fund.quartile}
            </Badge>
          ) : (
            "-"
          )}
        </td>
      </tr>
      <tr>
        <td colSpan={6} className="px-4 pb-3">
          <div className="ml-auto w-full max-w-142 rounded-lg bg-tag-slate px-4 py-3 text-xs text-tag-slate-foreground">
            {fund.reasons
              .map((r, i) => `${ROMAN[i]}) ${REASON_LABEL[r]}`)
              .join("; ")}
          </div>
        </td>
      </tr>
    </tbody>
  );
}
